import { Form } from 'antd';
import { useMemo } from 'react';

import { useKimLhcAutoCalculate, BaseCalculationPayload } from '@/components/views/Report/KimLhc/hooks/useKimLhcAutoCalculate';
import { FieldNamesEnum } from '@/components/views/Report/KimLhc/types';

interface CalculatedScores {
	workConditionScore: number;
	manScore: number;
	womanScore: number;
}

interface WorkConditionFormData {
	work_condition_hands_position?: string | null;
	work_condition_transfer?: string | null;
	work_condition_spatial_condition?: string | null;
	work_condition_adverse?: string | null;
	work_condition_clothes?: string | null;
	work_condition_hold_load?: string | null;
}

interface CalculateScoresResponse {
	work_condition_score: number;
	man_score: number;
	woman_score: number;
}

const INITIAL_SCORES: CalculatedScores = {
	workConditionScore: 0,
	manScore: 0,
	womanScore: 0
};

function buildPayload(formData: WorkConditionFormData, basePayload: BaseCalculationPayload) {
	return { ...basePayload, ...formData };
}

function transformScores(scores: CalculateScoresResponse): CalculatedScores {
	return {
		workConditionScore: scores.work_condition_score,
		manScore: scores.man_score,
		womanScore: scores.woman_score
	};
}

export function useAutoCalculate() {
	const form = Form.useFormInstance();
	const WORK_CONDITION_GROUP = FieldNamesEnum.WORK_CONDITION;

	const handsPosition = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_hands_position'], form) ?? null;
	const transfer = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_transfer'], form) ?? null;
	const spatialCondition = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_spatial_condition'], form) ?? null;
	const adverse = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_adverse'], form) ?? null;
	const clothes = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_clothes'], form) ?? null;
	const holdLoad = Form.useWatch([WORK_CONDITION_GROUP, 'work_condition_hold_load'], form) ?? null;

	const watchedValues = useMemo(
		() => ({
			work_condition_hands_position: handsPosition,
			work_condition_transfer: transfer,
			work_condition_spatial_condition: spatialCondition,
			work_condition_adverse: adverse,
			work_condition_clothes: clothes,
			work_condition_hold_load: holdLoad
		}),
		[handsPosition, transfer, spatialCondition, adverse, clothes, holdLoad]
	);

	return useKimLhcAutoCalculate<WorkConditionFormData, CalculatedScores, CalculateScoresResponse>({
		watchedValues,
		initialScores: INITIAL_SCORES,
		buildPayload,
		transformScores
	});
}
